// Content for the Permis ATNA hero. All interface figures are illustrative.
export type Bi = { fr: string; en: string }

export const PERMIS_IMAGES = {
  house: 'https://cdn.magicpatterns.com/patterns/generated-images/53ca5818-b6e0-4051-ac38-c144d94ab23c.jpg',
  avatar: 'https://cdn.magicpatterns.com/patterns/generated-images/8604a75f-fffa-41a4-b6a9-101ab8fd41cb.jpg',
  parcel: 'https://cdn.magicpatterns.com/patterns/generated-images/8436ee4c-46b8-484c-9cd2-0e1cd010e267.jpg',
}

export const permisCopy = {
  eyebrow: { fr: 'Permis ATNA', en: 'ATNA Permits' },
  line1: { fr: 'Du projet au permis.', en: 'From project to permit.' },
  line2: { fr: 'Plus simple pour', en: 'Simpler for' },
  line3: { fr: 'les citoyens. Plus efficace', en: 'residents. More efficient' },
  line4: { fr: 'pour vos équipes.', en: 'for your teams.' },
  body: {
    fr: 'Permis ATNA guide les citoyens, vérifie l’information, prépare une demande complète et orchestre tout le processus — de la première question jusqu’à la délivrance du permis.',
    en: 'ATNA Permits guides residents, verifies information, prepares a complete application and orchestrates the entire process — from the first question to permit issuance.',
  },
  primary: { fr: 'Voir Permis ATNA en action', en: 'See ATNA Permits in action' },
  secondary: { fr: 'Parler à notre équipe', en: 'Talk to our team' },
  capabilities: {
    fr: ['Intelligence vérifiée', 'Formulaires guidés', 'Documents', 'Paiements', 'Flux de travail', 'Inspections'],
    en: ['Verified intelligence', 'Guided forms', 'Documents', 'Payments', 'Workflow', 'Inspections'],
  },
}

export const questionCopy: Bi = {
  fr: 'Je veux construire une terrasse de 16 × 20 pieds. Est-ce que je peux ?',
  en: 'I want to build a 16 × 20 ft deck. Am I allowed?',
}

export const processSteps: { key: 'home' | 'doc' | 'pay'; title: Bi; sub: Bi }[] = [
  { key: 'home', title: { fr: 'Admissibilité vérifiée', en: 'Eligibility verified' }, sub: { fr: 'Règlement de zonage · version en vigueur', en: 'Zoning by-law · current version' } },
  { key: 'doc', title: { fr: 'Documents préparés', en: 'Documents prepared' }, sub: { fr: 'Liste de vérification personnalisée',en: 'Personalized checklist' } },
  { key: 'pay', title: { fr: 'Paiement effectué', en: 'Payment completed' }, sub: { fr: 'Demande transmise', en: 'Application submitted' } },
]

export const propertyCopy = {
  items: [
    { fr: 'Propriété et zonage identifiés', en: 'Property and zoning identified' },
    { fr: 'Règlements applicables', en: 'Applicable by-laws' },
    { fr: 'Contraintes (marges, rives, etc.)', en: 'Constraints (setbacks, shorelines, etc.)' },
    { fr: 'Historique des permis', en: 'Permit history' },
  ] as Bi[],
}

export const phoneCopy = {
  title: { fr: 'Ma demande de permis', en: 'My permit application' },
  steps: [
    { fr: 'Admissibilité', en: 'Eligibility' },
    { fr: 'Documents', en: 'Documents' },
    { fr: 'Paiement', en: 'Payment' },
    { fr: 'Soumission', en: 'Submission' },
  ] as Bi[],
  activeStep: 1,
  docsTitle: { fr: 'Documents requis', en: 'Required documents' },
  docsCount: { fr: '3 sur 4 fournis', en: '3 of 4 completed' },
  docs: [
    { name: { fr: 'Plan d’implantation', en: 'Site plan' }, meta: { fr: 'PDF · 1,2 Mo', en: 'PDF · 1.2 MB' }, kind: 'file' },
    { name: { fr: 'Plans de construction', en: 'Construction plans' }, meta: { fr: 'PDF · 2,1 Mo', en: 'PDF · 2.1 MB' }, kind: 'file' },
    { name: { fr: 'Photos du site', en: 'Site photos' }, meta: { fr: '3 photos', en: '3 photos' }, kind: 'photo' },
  ] as { name: Bi; meta: Bi; kind: 'file' | 'photo' }[],
  pending: { name: { fr: 'Formulaire complémentaire', en: 'Supplementary form' }, meta: { fr: 'À compléter', en: 'To complete' } },
  submit: { fr: 'Soumettre ma demande', en: 'Submit my application' },
}

export const dashboardCopy = {
  city: 'Brossard',
  nav: [
    { fr: 'Tableau de bord', en: 'Dashboard' },
    { fr: 'Demandes de permis', en: 'Permit applications' },
    { fr: 'Inspections', en: 'Inspections' },
    { fr: 'Paiements', en: 'Payments' },
    { fr: 'Documents', en: 'Documents' },
    { fr: 'Analytique', en: 'Analytics' },
  ] as Bi[],
  title: { fr: 'Demandes de permis', en: 'Permit applications' },
  period: { fr: '30 derniers jours', en: 'Last 30 days' },
  kpis: [
    { label: { fr: 'Demandes reçues', en: 'Applications received' }, value: '245', trend: { fr: '+32 %', en: '+32%' } },
    { label: { fr: 'En traitement', en: 'In progress' }, value: '178', trend: null },
    { label: { fr: 'Délai moyen', en: 'Average time' }, value: { fr: '12 jours', en: '12 days' }, trend: { fr: '+40 %', en: '+40%' } },
  ] as { label: Bi; value: string | Bi; trend: Bi | null }[],
  tabs: [
    { fr: 'Par étape', en: 'By stage' },
    { fr: 'Par type', en: 'By type' },
    { fr: 'Par secteur', en: 'By sector' },
  ] as Bi[],
  stages: [
    { label: { fr: 'Réception et prévalidation', en: 'Intake & pre-validation' }, pct: 100, count: 72, dot: '#EF4444' },
    { label: { fr: 'Analyse en urbanisme', en: 'Planning review' }, pct: 65, count: 47, dot: '#F59E0B' },
    { label: { fr: 'Analyse en ingénierie', en: 'Engineering review' }, pct: 48, count: 35, dot: '#22A04F' },
    { label: { fr: 'Inspection', en: 'Inspection' }, pct: 30, count: 22, dot: '#F59E0B' },
    { label: { fr: 'Décision et émission', en: 'Decision & issuance' }, pct: 22, count: 16, dot: '#F59E0B' },
  ] as { label: Bi; pct: number; count: number; dot: string }[],
}

export const verifiedCopy = {
  title: { fr: 'Réponse vérifiée par ATNA', en: 'Answer verified by ATNA' },
  line1: { fr: 'Règlement de zonage · Ville de Brossard', en: 'Zoning by-law · City of Brossard' },
  line2: { fr: 'Version en vigueur · Vérifié aujourd’hui', en: 'Current version · Verified today' },
  confidence: { fr: 'Confiance élevée', en: 'High confidence' },
  score: 4,
}

export const permisBenefits: { key: 'people' | 'clock' | 'gear' | 'chart'; title: Bi; body: Bi }[] = [
  { key: 'people', title: { fr: 'Des citoyens mieux guidés', en: 'Better-guided residents' }, body: { fr: 'Moins de questions, moins d’erreurs, plus de satisfaction.', en: 'Fewer questions, fewer errors, more satisfaction.' } },
  { key: 'clock', title: { fr: 'Moins d’appels et de ressaisies', en: 'Fewer calls and less re-keying' }, body: { fr: 'Des demandes complètes du premier coup.', en: 'Complete applications the first time.' } },
  { key: 'gear', title: { fr: 'Des processus plus rapides', en: 'Faster processes' }, body: { fr: 'Des délais réduits et une meilleure coordination.', en: 'Shorter timelines and better coordination.' } },
  { key: 'chart', title: { fr: 'Une visibilité en temps réel', en: 'Real-time visibility' }, body: { fr: 'Des données pour décider et mieux planifier.', en: 'Data to decide and plan better.' } },
]
